import React, { useState } from 'react';
import '../styles/Tour.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSmile, faStar, faUser, faCheckCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

interface TourStep {
  title: React.ReactNode;
  description: string;
  element?: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    title: <><FontAwesomeIcon icon={faSmile} /> Bem-vindo ao My Money!</>,
    description: 'Vamos fazer um tour rápido para você conhecer as principais funcionalidades. Você pode pular a qualquer momento.',
  },
  {
    title: ' Dashboard',
    description: 'Aqui você vê um resumo completo das suas finanças: receita total, lucro, margem média e quantidade de serviços.',
  },
  {
    title: <><FontAwesomeIcon icon={faPlus} /> Adicionar Serviços</>,
    description: 'Clique em "Adicionar Serviço" para registrar seus trabalhos. Informe o nome, cliente (opcional), valor e margem de lucro.',
  },

  {
    title: ' Lançamentos Financeiros',
    description: 'Acesse a lista completa de serviços cadastrados. Você pode visualizar detalhes e excluir registros.',
  },
  {
    title: ' Relatórios',
    description: 'Analise seus dados com gráficos e relatórios detalhados por período (dia, semana, mês ou ano).',
  },
  {
    title: <><FontAwesomeIcon icon={faStar} /> Premium</>,
    description: 'Upgrade para Premium por apenas R$ 2,99/mês e tenha acesso a sugestões de investimentos com IA!',
  },
  {
    title: <><FontAwesomeIcon icon={faUser} /> Perfil</>,
    description: 'Configure seus dados pessoais e compartilhe seu código de indicação. A cada 5 indicações, ganhe 1 mês grátis!',
  },

  {
    title: <><FontAwesomeIcon icon={faCheckCircle} /> Pronto!</>,
    description: 'Agora você já sabe como usar o My Money. Comece adicionando seu primeiro serviço!',
  },

];

interface TourProps {
  onComplete: () => void;
  onSkip: () => void;
}

const Tour: React.FC<TourProps> = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showDontShowAgain, setShowDontShowAgain] = useState(false);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    if (showDontShowAgain) {
      localStorage.setItem('tour_completed', 'true');
    }
    onComplete();
  };

  const handleSkipTour = () => {
    if (window.confirm('Deseja pular o tour? Você pode acessá-lo novamente nas configurações.')) {
      if (showDontShowAgain) {
        localStorage.setItem('tour_completed', 'true');
      }
      onSkip();
    }
  };

  const handleDontShowAgainChange = (checked: boolean) => {
    setShowDontShowAgain(checked);
    if (checked) {
      localStorage.setItem('tour_completed', 'true');
    } else {
      localStorage.removeItem('tour_completed');
    }
  };

  const step = TOUR_STEPS[currentStep];

  return (
    <div className="tour-overlay">
      <div className="tour-modal">
        <div className="tour-header">
          <h2>{step.title}</h2>
          <button className="tour-close" onClick={handleSkipTour}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="tour-body">
          <p>{step.description}</p>
        </div>

        <div className="tour-footer">
          <div className="tour-progress">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={`tour-dot ${index === currentStep ? 'active' : ''} ${
                  index < currentStep ? 'completed' : ''
                }`}
              />
            ))}
          </div>

          <div className="tour-actions">
            <label className="dont-show-again">
              <input
                type="checkbox"
                checked={showDontShowAgain}
                onChange={(e) => handleDontShowAgainChange(e.target.checked)}
              />
              <span>Não mostrar novamente</span>
            </label>

            <div className="tour-buttons">
              <button
                className="tour-btn secondary"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                ← Anterior
              </button>
              <button className="tour-btn primary" onClick={handleNext}>
                {currentStep === TOUR_STEPS.length - 1 ? 'Começar! ' : 'Próximo →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tour;
